import React, { useState, useEffect, useRef } from 'react';
import { useComparison } from '../contexts/ComparisonContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import SEO from '../components/SEO';
import { getFirestore, collection, addDoc, doc, setDoc, query, where, getDocs } from 'firebase/firestore';
import { showNotification } from '../components/Notification';
import BreadcrumbNav from '../components/seo/BreadcrumbNav';
import ComparisonSkeleton from '../components/ComparisonSkeleton';
import PreLoader from '../components/PreLoader';
import { buildPropertyPath } from '../utils/seo/urlHelpers';
function Comparison() {
  const { 
    comparisonProperties, 
    removeFromComparison, 
    clearComparison, 
    loading 
  } = useComparison();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isPreLoading, setIsPreLoading] = useState(true);
  
  // Estados para compartilhamento
  const [, setShareModal] = useState({ isOpen: false, link: '' });
  const [shareListModal, setShareListModal] = useState({ isOpen: false, link: '', isUpdate: false });
  const [isSharing, setIsSharing] = useState(false);
  const shareModalRef = useRef(null);
  const shareListModalRef = useRef(null);

  const handlePreLoadingComplete = () => {
    setIsPreLoading(false);
  };

  // Função para fechar modais ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (shareModalRef.current && !shareModalRef.current.contains(event.target)) {
        setShareModal({ isOpen: false, link: '' });
      }
      if (shareListModalRef.current && !shareListModalRef.current.contains(event.target)) {
        setShareListModal({ isOpen: false, link: '', isUpdate: false });
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [setShareModal, setShareListModal]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!loading) {
      setIsPreLoading(false);
    }
  }, [loading]);

  const formatPrice = (price, purpose) => {
    if (purpose === 'sale') {
      return `R$ ${Number(price).toLocaleString()}`;
    }
    return `R$ ${Number(price).toLocaleString()}/mês`;
  };

  const getPropertyType = (type) => {
    const types = {
      house: 'Casa',
      apartment: 'Apartamento',
      loft: 'Loft',
      warehouse: 'Galpão',
      store: 'Loja',
      building: 'Prédio',
      office: 'Sala',
      land: 'Terreno',
      farm: 'Sítio',
      countryHouse: 'Chácara'
    };
    return types[type] || 'Imóvel';
  };

  // Função para extrair características do imóvel
  const getPropertyFeatures = (details) => {
    if (!details) return [];
    
    const featureLabels = {
      garage: 'Garagem',
      coveredGarage: 'Garagem Coberta',
      elevator: 'Elevador',
      suite: 'Suíte',
      serviceArea: 'Área de Serviço',
      kitchenCabinets: 'Armários na Cozinha',
      bedroomCabinets: 'Armários no Quarto',
      furnished: 'Mobiliado',
      semiFurnished: 'Semi-mobiliado',
      airConditioning: 'Ar Condicionado',
      barbecue: 'Churrasqueira',
      balcony: 'Varanda',
      serviceRoom: 'Dependência de Empregada',
      office: 'Escritório',
      smart: 'Casa Inteligente',
      smokeAlarm: 'Alarme de Fumaça',
      pool: 'Piscina',
      partyArea: 'Área de Festas'
    };

    return Object.entries(details)
      .filter(([key, value]) => value === true)
      .map(([key]) => featureLabels[key] || key);
  };

  // Função para extrair características do condomínio
  const getCondominiumFeatures = (condoDetails) => {
    if (!condoDetails) return [];
    
    const featureLabels = {
      gatedCommunity: 'Condomínio Fechado',
      security24h: 'Segurança 24h',
      walledArea: 'Área Murada',
      petsAllowed: 'Permite Pets',
      electronicGate: 'Portão Eletrônico',
      gym: 'Academia',
      pool: 'Piscina',
      kidPool: 'Piscina Infantil',
      partyRoom: 'Salão de Festas',
      gourmetKitchen: 'Cozinha Gourmet',
      hotSauna: 'Sauna a Vapor',
      coldSauna: 'Sauna Seca',
      doorman: 'Portaria'
    };

    return Object.entries(condoDetails)
      .filter(([key, value]) => value === true)
      .map(([key]) => featureLabels[key] || key);
  };

  // Função para compartilhar lista de comparação
  const shareComparisonList = async () => {
    if (comparisonProperties.length === 0) {
      showNotification('Não há imóveis na comparação para compartilhar', 'warning', 4000);
      return;
    }

    if (!user) {
      navigate('/login', {
        state: {
          from: location,
          message:
            'Para compartilhar sua comparação de imóveis, você precisa fazer login ou criar uma conta. Sua comparação salva localmente será preservada após o login.',
        },
      });
      return;
    }

    setIsSharing(true);
    try {
      const db = getFirestore();
      const currentPropertyIds = comparisonProperties.map(property => property.id).sort();
      
      // Verifica se já existe uma lista com as mesmas propriedades para este usuário
      const existingListsQuery = query(
        collection(db, 'sharedLists'),
        where('userId', '==', user.uid),
        where('type', '==', 'comparison'),
        where('isActive', '==', true)
      );
      
      const existingListsSnapshot = await getDocs(existingListsQuery);
      let existingListId = null;
      
      // Verifica se alguma lista existente tem as mesmas propriedades
      for (const doc of existingListsSnapshot.docs) {
        const listData = doc.data();
        const existingPropertyIds = listData.propertyIds?.sort() || [];
        
        // Compara os arrays de IDs
        if (existingPropertyIds.length === currentPropertyIds.length &&
            existingPropertyIds.every((id, index) => id === currentPropertyIds[index])) {
          existingListId = doc.id;
          break;
        }
      }
      
      // Se não encontrou lista com as mesmas propriedades, verifica o limite de 10 links ativos
      if (!existingListId && existingListsSnapshot.docs.length >= 10) {
        showNotification('Você já possui 10 links de comparação ativos.', 'warning', 5000);
        setIsSharing(false);
        return;
      }
      
      let shareLink;
      
      if (existingListId) {
        // Atualiza a expiração da lista existente
        await setDoc(doc(db, 'sharedLists', existingListId), {
          expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000), // 24 horas
          updatedAt: new Date()
        }, { merge: true });
        
        shareLink = `${window.location.origin}/lista-compartilhada/${existingListId}`;
        setShareListModal({ isOpen: true, link: shareLink, isUpdate: true });
      } else {
        // Cria uma nova lista
        const sharedListData = {
          userId: user.uid,
          userName: user.name || user.email?.split('@')[0] || 'Usuário',
          userEmail: user.email,
          type: 'comparison',
          propertyIds: currentPropertyIds,
          createdAt: new Date(),
          expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000), // 24 horas
          isActive: true
        };

        const docRef = await addDoc(collection(db, 'sharedLists'), sharedListData);
        shareLink = `${window.location.origin}/lista-compartilhada/${docRef.id}`;
        setShareListModal({ isOpen: true, link: shareLink, isUpdate: false });
      }
      } catch (error) {
        console.error('Erro ao compartilhar lista:', error);
        showNotification('Erro ao compartilhar lista. Tente novamente.', 'error', 5000);
      } finally {
        setIsSharing(false);
      }
  };

  // Função para copiar link
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      showNotification('Link copiado para a área de transferência!', 'success', 3000);
    } catch (err) {
      console.error('Erro ao copiar:', err);
      // Fallback para navegadores mais antigos
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      showNotification('Link copiado para a área de transferência!', 'success', 3000);
    }
  };

  const showSkeleton = loading && comparisonProperties.length === 0;
  const showEmpty = !showSkeleton && comparisonProperties.length === 0;

  return (
    <>
      <PreLoader
        isLoading={isPreLoading}
        onLoadingComplete={handlePreLoadingComplete}
      />
      <SEO
        title="Comparação de Imóveis"
        description="Compare imóveis e encontre o melhor para você"
        keywords="comparação de imóveis, comparar propriedades"
        canonicalUrl="/comparacao"
        pageType="website"
        robots="noindex,nofollow"
      />

      {showSkeleton ? (
        <ComparisonSkeleton />
      ) : showEmpty ? (
        <div className="min-h-screen bg-gray-50 py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <div className="mx-auto h-24 w-24 text-gray-400 mb-6">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
                </svg>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                Nenhum imóvel para comparar
              </h1>
              <p className="text-lg text-gray-600 mb-8">
                Adicione imóveis à comparação para ver as diferenças entre eles
              </p>
              <Link
                to="/propriedades"
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Ver Propriedades
              </Link>
            </div>
          </div>
        </div>
      ) : (
    <div className="min-h-screen bg-gray-50 py-8" style={{ fontFamily: 'Montserrat, sans-serif' }}>
      <div className="max-w-[85rem] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumbs */}
        <div className="mb-8">
          <BreadcrumbNav
            items={[
              { name: 'Início', path: '/' },
              { name: 'Propriedades', path: '/propriedades' },
              { name: 'Comparação de Imóveis', current: true },
            ]}
          />
        </div>
              {/* Header */}
              <div className="mb-8">
                {/* Título e botão - mesma linha em telas grandes, empilhados no mobile */}
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
                  <h1 className="text-3xl font-bold text-gray-900 text-center lg:text-left">
                    Comparação de Imóveis
                  </h1>
                  
                  {/* Botões de ação */}
                  <div className="flex flex-col sm:flex-row gap-2">
                    <button
                      onClick={shareComparisonList}
                      disabled={isSharing || comparisonProperties.length === 0}
                      className="rounded-lg px-4 py-2 text-sm font-medium transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{
                        backgroundColor: 'var(--btn-outline-bg, #ffffff)',
                        color: 'var(--btn-outline-text, #3B82F6)',
                        border: '1px solid var(--btn-outline-border, #3B82F6)'
                      }}
                      onMouseEnter={(e) => {
                        if (!e.currentTarget.disabled) {
                          e.currentTarget.style.backgroundColor = 'var(--btn-outline-hover-bg, #3B82F6)';
                          e.currentTarget.style.color = 'var(--btn-outline-hover-text, #ffffff)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!e.currentTarget.disabled) {
                          e.currentTarget.style.backgroundColor = 'var(--btn-outline-bg, #ffffff)';
                          e.currentTarget.style.color = 'var(--btn-outline-text, #3B82F6)';
                        }
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
                      </svg>
                      {isSharing ? 'Compartilhando...' : 'Compartilhar Comparação'}
                    </button>

                    <button
                      onClick={async () => {
                        const result = await clearComparison();
                        if (result.success) {
                          showNotification(result.message, 'success', 3000);
                        }
                      }}
                      className="px-4 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-300 rounded-md hover:bg-red-100 transition-colors flex items-center justify-center gap-2"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                      </svg>
                      Limpar Comparação
                    </button>
                  </div>
                </div>
                
                {/* Contador - em uma linha separada */}
                <div>
                  <p className="text-gray-600">
                    Compare {comparisonProperties.length} {comparisonProperties.length === 1 ? 'imóvel' : 'imóveis'} selecionado{comparisonProperties.length > 1 ? 's' : ''}
                  </p>
                </div>

                {/* Aviso para usuários não logados */}
                {!user && comparisonProperties.length > 0 && (
                  <div className="mt-6">
                    <div className="inline-flex items-center gap-2 px-4 py-3 bg-orange-100 text-orange-700 rounded-lg border border-orange-400">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 flex-shrink-0">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
                      </svg>
                      <span className="text-sm">
                        Sua comparação está salva localmente.{' '}
                        <button
                          onClick={() => navigate('/login', { state: { from: location } })}
                          className="font-bold underline hover:text-orange-900 transition-colors inline-flex items-center gap-1"
                        >
                          Faça login
                        </button>
                        {' '}para salvá-la permanentemente e compartilhar.
                      </span>
                    </div>
                  </div>
                )}
              </div>

        {/* Comparison Table */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                    Propriedade
                  </th>
                  {comparisonProperties.map((property) => (
                    <th key={property.id} className="px-2 py-4 text-center text-sm font-medium text-gray-500 uppercase tracking-wider">
                      <div className="flex flex-col items-center">
                        <div className="w-56 h-32 mb-2 rounded-lg overflow-hidden bg-gray-200">
                          <LazyLoadImage
                            src={property.coverImage || property.images[0]}
                            alt={property.title}
                            className="w-full h-full object-cover object-center"
                            effect="blur"
                            placeholder={
                              <div className="w-full h-full bg-gray-200 animate-pulse flex items-center justify-center">
                                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                              </div>
                            }
                          />
                        </div>
                        <span className="text-xs text-gray-600">{property.code}</span>
                        <button
                          type="button"
                          onClick={() => removeFromComparison(property.id)}
                          className="mt-2 px-3 py-1.5 text-xs font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors"
                        >
                          Remover
                        </button>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {/* Título */}
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    Título
                  </td>
                  {comparisonProperties.map((property) => (
                    <td key={property.id} className="px-2 py-4 text-sm text-gray-900 text-center">
                      <Link
                        to={buildPropertyPath(property)}
                        className="text-blue-600 hover:text-blue-800 transition-colors"
                      >
                        {property.title}
                      </Link>
                    </td>
                  ))}
                </tr>

                {/* Preço */}
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    Preço
                  </td>
                  {comparisonProperties.map((property) => (
                    <td key={property.id} className="px-2 py-4 text-sm text-gray-900 text-center font-semibold">
                      {formatPrice(property.price, property.purpose)}
                    </td>
                  ))}
                </tr>

                {/* Tipo */}
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    Tipo
                  </td>
                  {comparisonProperties.map((property) => (
                    <td key={property.id} className="px-2 py-4 text-sm text-gray-900 text-center">
                      {getPropertyType(property.type)}
                    </td>
                  ))}
                </tr>

                {/* Finalidade */}
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    Finalidade
                  </td>
                  {comparisonProperties.map((property) => (
                    <td key={property.id} className="px-2 py-4 text-sm text-gray-900 text-center">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        property.purpose === 'sale' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {property.purpose === 'sale' ? 'Venda' : 'Aluguel'}
                      </span>
                    </td>
                  ))}
                </tr>

                {/* Endereço */}
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    Endereço
                  </td>
                  {comparisonProperties.map((property) => (
                    <td key={property.id} className="px-2 py-4 text-sm text-gray-900 text-center">
                      {property.address}
                    </td>
                  ))}
                </tr>

                {/* Quartos */}
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    Quartos
                  </td>
                  {comparisonProperties.map((property) => (
                    <td key={property.id} className="px-2 py-4 text-sm text-gray-900 text-center">
                      {property.bedrooms || '-'}
                    </td>
                  ))}
                </tr>

                {/* Suítes */}
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    Suítes
                  </td>
                  {comparisonProperties.map((property) => (
                    <td key={property.id} className="px-2 py-4 text-sm text-gray-900 text-center">
                      {property.suites || '-'}
                    </td>
                  ))}
                </tr>

                {/* Banheiros */}
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    Banheiros
                  </td>
                  {comparisonProperties.map((property) => (
                    <td key={property.id} className="px-2 py-4 text-sm text-gray-900 text-center">
                      {property.bathrooms || '-'}
                    </td>
                  ))}
                </tr>

                {/* Vagas */}
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    Vagas
                  </td>
                  {comparisonProperties.map((property) => (
                    <td key={property.id} className="px-2 py-4 text-sm text-gray-900 text-center">
                      {property.parkingSpaces || '-'}
                    </td>
                  ))}
                </tr>

                {/* Área */}
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    Área (m²)
                  </td>
                  {comparisonProperties.map((property) => (
                    <td key={property.id} className="px-2 py-4 text-sm text-gray-900 text-center">
                      {property.area ? `${property.area} m²` : '-'}
                    </td>
                  ))}
                </tr>

                {/* Condomínio Fechado */}
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    Condomínio Fechado
                  </td>
                  {comparisonProperties.map((property) => (
                    <td key={property.id} className="px-2 py-4 text-sm text-gray-900 text-center">
                      {property.isGatedCommunity ? 'Sim' : 'Não'}
                    </td>
                  ))}
                </tr>

                {/* IPTU */}
                {comparisonProperties.some(p => p.iptuFee && p.iptuFee > 0) && (
                  <tr>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      IPTU
                    </td>
                    {comparisonProperties.map((property) => (
                      <td key={property.id} className="px-2 py-4 text-sm text-gray-900 text-center">
                        {property.iptuFee && property.iptuFee > 0 ? `R$ ${Number(property.iptuFee).toLocaleString()}` : '-'}
                      </td>
                    ))}
                  </tr>
                )}

                {/* Condomínio */}
                {comparisonProperties.some(p => p.condoFee && p.condoFee > 0) && (
                  <tr className="bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      Condomínio
                    </td>
                    {comparisonProperties.map((property) => (
                      <td key={property.id} className="px-2 py-4 text-sm text-gray-900 text-center">
                        {property.condoFee && property.condoFee > 0 ? `R$ ${Number(property.condoFee).toLocaleString()}` : '-'}
                      </td>
                    ))}
                  </tr>
                )}

                {/* Características do Imóvel */}
                {comparisonProperties.some(p => getPropertyFeatures(p.details).length > 0) && (
                  <>
                    {/* Cabeçalho da seção */}
                    <tr>
                      <td colSpan={comparisonProperties.length + 1} className="px-6 py-3 text-sm font-bold text-gray-900 bg-blue-100">
                        Características do Imóvel
                      </td>
                    </tr>
                    
                    {/* Lista de todas as características possíveis */}
                    {(() => {
                      // Coleta todas as características únicas de todos os imóveis
                      const allFeatures = new Set();
                      comparisonProperties.forEach(property => {
                        const features = getPropertyFeatures(property.details);
                        features.forEach(feature => allFeatures.add(feature));
                      });
                      
                      // Mapeia as características para suas chaves originais
                      const featureKeyMap = {
                        'Garagem': 'garage',
                        'Garagem Coberta': 'coveredGarage',
                        'Elevador': 'elevator',
                        'Suíte': 'suite',
                        'Área de Serviço': 'serviceArea',
                        'Armários na Cozinha': 'kitchenCabinets',
                        'Armários no Quarto': 'bedroomCabinets',
                        'Mobiliado': 'furnished',
                        'Semi-mobiliado': 'semiFurnished',
                        'Ar Condicionado': 'airConditioning',
                        'Churrasqueira': 'barbecue',
                        'Varanda': 'balcony',
                        'Dependência de Empregada': 'serviceRoom',
                        'Escritório': 'office',
                        'Casa Inteligente': 'smart',
                        'Alarme de Fumaça': 'smokeAlarm',
                        'Piscina': 'pool',
                        'Área de Festas': 'partyArea'
                      };
                      
                      return Array.from(allFeatures).map((feature, index) => (
                        <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-6 py-3 text-sm font-medium text-gray-900">
                            {feature}
                          </td>
                          {comparisonProperties.map((property) => {
                            const featureKey = featureKeyMap[feature];
                            const hasFeature = property.details && property.details[featureKey] === true;
                            return (
                              <td key={property.id} className="px-2 py-3 text-sm text-gray-900 text-center">
                                {hasFeature ? (
                                  <span className="inline-flex items-center justify-center w-6 h-6 bg-green-100 text-green-800 rounded-full text-sm font-bold">
                                    ✓
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center justify-center w-6 h-6 bg-red-100 text-red-800 rounded-full text-sm font-bold">
                                    ✗
                                  </span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ));
                    })()}
                  </>
                )}

                {/* Características do Condomínio */}
                {comparisonProperties.some(p => getCondominiumFeatures(p.condoDetails).length > 0) && (
                  <>
                    {/* Cabeçalho da seção */}
                    <tr className="bg-gray-50">
                      <td colSpan={comparisonProperties.length + 1} className="px-6 py-3 text-sm font-bold text-gray-900 bg-green-100">
                        Características do Condomínio
                      </td>
                    </tr>
                    
                    {/* Lista de todas as características possíveis */}
                    {(() => {
                      // Coleta todas as características únicas de todos os imóveis
                      const allFeatures = new Set();
                      comparisonProperties.forEach(property => {
                        const features = getCondominiumFeatures(property.condoDetails);
                        features.forEach(feature => allFeatures.add(feature));
                      });
                      
                      // Mapeia as características para suas chaves originais
                      const featureKeyMap = {
                        'Condomínio Fechado': 'gatedCommunity',
                        'Segurança 24h': 'security24h',
                        'Área Murada': 'walledArea',
                        'Permite Pets': 'petsAllowed',
                        'Portão Eletrônico': 'electronicGate',
                        'Academia': 'gym',
                        'Piscina': 'pool',
                        'Piscina Infantil': 'kidPool',
                        'Salão de Festas': 'partyRoom',
                        'Cozinha Gourmet': 'gourmetKitchen',
                        'Sauna a Vapor': 'hotSauna',
                        'Sauna Seca': 'coldSauna',
                        'Portaria': 'doorman'
                      };
                      
                      return Array.from(allFeatures).map((feature, index) => (
                        <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-6 py-3 text-sm font-medium text-gray-900">
                            {feature}
                          </td>
                          {comparisonProperties.map((property) => {
                            const featureKey = featureKeyMap[feature];
                            const hasFeature = property.condoDetails && property.condoDetails[featureKey] === true;
                            return (
                              <td key={property.id} className="px-2 py-3 text-sm text-gray-900 text-center">
                                {hasFeature ? (
                                  <span className="inline-flex items-center justify-center w-6 h-6 bg-green-100 text-green-800 rounded-full text-sm font-bold">
                                    ✓
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center justify-center w-6 h-6 bg-red-100 text-red-800 rounded-full text-sm font-bold">
                                    ✗
                                  </span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ));
                    })()}
                  </>
                )}

                {/* Ações */}
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    Ações
                  </td>
                  {comparisonProperties.map((property) => (
                    <td key={property.id} className="px-2 py-4 text-center">
                      <Link
                        to={buildPropertyPath(property)}
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors"
                      >
                        Ver Detalhes
                      </Link>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-8 flex justify-center gap-4">
          <Link
            to="/propriedades"
            className="inline-flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Adicionar Mais Imóveis
          </Link>
        </div>
      </div>

      {/* Modal de Compartilhamento de Lista */}
      {shareListModal.isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
          onClick={() => setShareListModal({ isOpen: false, link: '', isUpdate: false })}
        >
          <div 
            ref={shareListModalRef}
            className="bg-white rounded-lg p-6 max-w-md w-full mx-4" 
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">Compartilhar Lista</h3>
              <button
                onClick={() => setShareListModal({ isOpen: false, link: '', isUpdate: false })}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                <strong>Comparação de Imóveis de {user?.name || 'Usuário'}</strong>
              </p>
              <p className="text-xs text-gray-500 mb-4">
                {comparisonProperties.length} imóveis na lista
              </p>
              
              <div className="bg-gray-50 p-3 rounded-lg mb-4">
                <p className="text-xs text-gray-500 mb-1">Link público:</p>
                <p className="text-sm text-gray-700 break-all">{shareListModal.link}</p>
              </div>
            </div>

            <div className="space-y-3">
              <button
                onClick={() => copyToClipboard(shareListModal.link)}
                className="w-full flex items-center justify-center gap-2 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-colors"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                <span className="text-sm font-medium">Copiar Link</span>
              </button>

              <button
                onClick={() => {
                  const message = `Confira minha comparação de imóveis: ${shareListModal.link}`;
                  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
                  window.open(whatsappUrl, '_blank');
                }}
                className="w-full flex items-center justify-center gap-2 p-3 rounded-lg border border-gray-200 hover:bg-green-50 hover:border-green-500 transition-colors"
              >
                <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.578 5.945L.057 24l6.243-1.654A11.882 11.882 0 0012.05 24c6.554 0 11.89-5.335 11.893-11.892A11.821 11.821 0 0020.463 5.703"/>
                </svg>
                <span className="text-sm font-medium">Enviar no WhatsApp</span>
              </button>
            </div>

            <div className="mt-4 p-3 bg-white rounded-lg">
              <p className="text-xs text-black">
                <strong>Dica:</strong> Este link é público e pode ser acessado por qualquer pessoa. A lista expira em <span className="text-red-600 font-semibold">24 horas</span>.
              </p>
            </div>
          </div>
        </div>
      )}

    </div>
      )}
    </>
  );
}

export default Comparison;
